import { NetworkStatus } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useCallback, useState } from 'react';
import { useBreadcrumbLabel } from '../../components/BreadcrumbLabelsContext.js';
import { assertUuidRouteParams } from '../../lib/assert-uuid-route-params.js';
import { BomDetailView } from '../../modules/bom/bom-detail.js';
import {
  GET_BOM,
  type BomDetail,
  type GetBomQueryResult,
  type GetBomQueryVariables,
} from '../../modules/bom/operations.js';

const BOM_ITEMS_PAGE_SIZE = 20;

export const Route = createFileRoute('/boms/$bomId')({
  beforeLoad: ({ params }) => assertUuidRouteParams(params),
  validateSearch: (search: Record<string, unknown>) => ({
    edit: search.edit === true || search.edit === 'true',
  }),
  component: BomDetailPage,
});

// eslint-disable-next-line react-refresh/only-export-components
function BomDetailPage() {
  const navigate = useNavigate();
  const { bomId } = Route.useParams();
  const { edit: startEditing } = Route.useSearch();
  // 'all' keeps partial data (e.g. a permission-denied field) instead of discarding the whole response.
  const { data, loading, error, refetch, fetchMore, networkStatus } = useQuery<
    GetBomQueryResult,
    GetBomQueryVariables
  >(GET_BOM, {
    variables: { id: bomId, first: BOM_ITEMS_PAGE_SIZE },
    errorPolicy: 'all',
  });
  const loadingMoreItems = networkStatus === NetworkStatus.fetchMore;
  const [loadMoreError, setLoadMoreError] = useState<string | null>(null);
  const [refetchError, setRefetchError] = useState<string | null>(null);

  // A failed refetch clears Apollo's `data`; fall back to the last good result so the view stays up.
  const [lastGoodBom, setLastGoodBom] = useState<BomDetail | null>(null);
  if (data?.bom && data.bom !== lastGoodBom) {
    setLastGoodBom(data.bom);
  }
  const bom = data?.bom ?? lastGoodBom;
  // Not `bom?.num` -- that can still be the PREVIOUS bomId's `lastGoodBom` while a new id's query is in flight.
  useBreadcrumbLabel(bomId, data?.bom?.num);

  const loadMoreItems = () => {
    const endCursor = bom?.bomItem.pageInfo.endCursor;
    if (!endCursor) return;
    setLoadMoreError(null);
    fetchMore({
      variables: { id: bomId, first: BOM_ITEMS_PAGE_SIZE, after: endCursor },
      updateQuery: (previous, { fetchMoreResult }) => {
        if (!fetchMoreResult?.bom || !previous.bom) return previous;
        const seenIds = new Set(previous.bom.bomItem.edges.map((edge) => edge.node.id));
        const newEdges = fetchMoreResult.bom.bomItem.edges.filter(
          (edge) => !seenIds.has(edge.node.id),
        );
        return {
          bom: {
            ...previous.bom,
            bomItem: {
              ...fetchMoreResult.bom.bomItem,
              edges: [...previous.bom.bomItem.edges, ...newEdges],
            },
          },
        };
      },
    }).catch(() => {
      setLoadMoreError('Failed to load more recipe items. Please try again.');
    });
  };

  const refetchLoadedItems = () => {
    // +1 so a newly-added item past the current page window is visible without "Load more".
    const first = Math.max(BOM_ITEMS_PAGE_SIZE, (bom?.bomItem.edges.length ?? 0) + 1);
    setRefetchError(null);
    // `errorPolicy: 'all'` makes `refetch` resolve with `result.error` instead of rejecting, so both paths are checked.
    refetch({ id: bomId, first })
      .then((result) => {
        if (result.error) {
          setRefetchError('Recipe may be out of date — refresh the page to see the latest.');
        }
      })
      .catch(() => {
        setRefetchError('Recipe may be out of date — refresh the page to see the latest.');
      });
  };

  const setEditingInUrl = useCallback(
    (editing: boolean) => {
      void navigate({
        to: '/boms/$bomId',
        params: { bomId },
        search: { edit: editing },
        replace: true,
      });
    },
    [navigate, bomId],
  );

  let content;
  // Keep showing data during background refetch — only the initial load uses a placeholder.
  if (loading && !bom) {
    content = <div className="text-sm text-muted-foreground">Loading...</div>;
  } else if (!bom && error) {
    content = <div className="text-sm text-destructive">Failed to load BOM. Please try again.</div>;
  } else if (!bom) {
    content = <div className="text-sm text-muted-foreground">BOM not found.</div>;
  } else {
    content = (
      <BomDetailView
        bom={bom}
        initialEditing={startEditing}
        onChanged={refetchLoadedItems}
        onEditingChange={setEditingInUrl}
        hasMoreItems={bom.bomItem.pageInfo.hasNextPage}
        loadingMoreItems={loadingMoreItems}
        onLoadMoreItems={loadMoreItems}
        loadMoreError={loadMoreError}
        refetchError={refetchError}
      />
    );
  }

  return <div>{content}</div>;
}
